import { useState, useEffect } from 'react';
import { useT } from '@baaligames/shared-ui/lib/i18n';
import Icon from '@baaligames/shared-ui/components/Icon';
import { useNavigate, useLocation, Routes, Route } from 'react-router-dom';
import MatchPage from './MatchPage';
import MyBets from './MyBets';
import Pnl from './Pnl';
import RulesButton from './Rules';
import { useWallet } from '@baaligames/shared-ui/lib/wallet';
import { api } from '@baaligames/shared-ui/lib/api';
import LiveOddsRow from '../../components/CricketOddsRow';

// "28 Jun, 1:00 PM" — shown on every lobby card so the date/time is always visible.
function fmtDateTime(s: string) {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '';
  return `${d.toLocaleDateString([], { day: '2-digit', month: 'short' })}, ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

// For a multi-day match at stumps: play is expected to resume the NEXT day at the
// match's scheduled daily start time. Returns e.g. "tomorrow, 1:00 PM" (estimate).
function expectedResume(startTime: string): string {
  if (!startTime) return '';
  const st = new Date(startTime);
  if (isNaN(st.getTime())) return '';
  const next = new Date();
  next.setHours(st.getHours(), st.getMinutes(), 0, 0);
  if (next.getTime() <= Date.now()) next.setDate(next.getDate() + 1);
  const isTomorrow = next.getDate() !== new Date().getDate();
  return `${isTomorrow ? 'tomorrow' : 'today'}, ${next.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

// Hand-drawn golden crown — worn by the WINNING team's logo on completed cards.
// Deliberately kept OUTSIDE the disabled/greyscale treatment: a winner stays lit.
function CrownBadge() {
  return (
    <span className="absolute -top-3 left-1/2 -translate-x-1/2 w-5 h-5 drop-shadow-md">
      <svg viewBox="0 0 24 24" className="w-5 h-5">
        <defs>
          <linearGradient id="crownGold" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#ffe27a" />
            <stop offset="55%" stopColor="#f5b91e" />
            <stop offset="100%" stopColor="#d99000" />
          </linearGradient>
        </defs>
        {/* band */}
        <rect x="5" y="16.4" width="14" height="3.2" rx="1.2" fill="url(#crownGold)" stroke="#a86e00" strokeWidth="0.7" />
        {/* three points with ball tips */}
        <path d="M5 16.5 L5 8.6 L9 12.2 L12 6.4 L15 12.2 L19 8.6 L19 16.5 Z"
              fill="url(#crownGold)" stroke="#a86e00" strokeWidth="0.8" strokeLinejoin="round" />
        <circle cx="5"  cy="7.6" r="1.15" fill="#ffe27a" stroke="#a86e00" strokeWidth="0.6" />
        <circle cx="12" cy="5.2" r="1.3"  fill="#ffe27a" stroke="#a86e00" strokeWidth="0.6" />
        <circle cx="19" cy="7.6" r="1.15" fill="#ffe27a" stroke="#a86e00" strokeWidth="0.6" />
        {/* jewels on the band */}
        <circle cx="9"  cy="18" r="0.8" fill="#e5484d" />
        <circle cx="12" cy="18" r="0.8" fill="#2f6fed" />
        <circle cx="15" cy="18" r="0.8" fill="#30a46c" />
      </svg>
    </span>
  );
}

function MatchList({ sport = 'cricket' }: { sport?: string }) {
  const t = useT();
  const nav = useNavigate();
  const wallet = useWallet();
  const [matches, setMatches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAllUpcoming, setShowAllUpcoming] = useState(false);
  const UPCOMING_LIMIT = 4;
  // Live odds for the lobby cards, keyed by matchId (from /cricket/state).
  const [oddsState, setOddsState] = useState<Record<string, any>>({});
  // Poll live odds whenever ANY match is in-play (its odds fill the cards; other
  // cards keep their dashed placeholders). No live match → no poll (cheap).
  const hasLiveMatch = matches.some((m) => m.status === 'in-play');

  // Continuous Countdown Component
  const CountdownTimer = ({ dateStr }: { dateStr: string }) => {
    const [timeText, setTimeText] = useState('');
  
    useEffect(() => {
      const updateTime = () => {
        const diff = new Date(dateStr).getTime() - new Date().getTime();
        if (diff > 0 && diff < 24 * 60 * 60 * 1000) {
          const hours = Math.floor(diff / (1000 * 60 * 60));
          const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
          const secs = Math.floor((diff % (1000 * 60)) / 1000);
          
          let text = 'Starts in ';
          if (hours > 0) text += `${hours}h `;
          text += `${mins}m ${secs}s`;
          setTimeText(text);
        } else {
          setTimeText(`Starts: ${new Date(dateStr).toLocaleDateString([], { month: 'short', day: 'numeric' })} ${new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`);
        }
      };
      
      updateTime();
      const interval = setInterval(updateTime, 1000);
      return () => clearInterval(interval);
    }, [dateStr]);
  
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-blue-500/20 text-[10px] font-bold text-blue-600 dark:text-blue-400">
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        {timeText}
      </span>
    );
  };

  // Match list + meta. Refetched every 15s so status / has_odds transitions
  // (a match going live and getting priced) surface without a manual reload.
  useEffect(() => {
    let cancelled = false;
    const load = (initial: boolean) => {
      if (initial) setLoading(true);
      api.get<{ success: boolean; matches: any[] }>(`/cricket/matches?sport=${sport}`)
        .then((res) => { if (!cancelled) setMatches(res.matches || []); })
        .catch(console.error)
        .finally(() => { if (initial && !cancelled) setLoading(false); });
    };
    load(true);
    const timer = setInterval(() => load(false), 15000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [sport]);

  // Live odds poll — only while a listed match is actually live-and-priced.
  // /cricket/state is a shared 1s-cached snapshot keyed by matchId (all sports),
  // so polling it every 2s is cheap at any scale.
  useEffect(() => {
    if (!hasLiveMatch) { setOddsState({}); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await api.get<{ success: boolean; state: any }>('/cricket/state');
        if (!cancelled && res?.state) setOddsState(res.state);
      } catch { /* keep last-good odds on a blip */ }
    };
    poll();
    const timer = setInterval(poll, 2000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [hasLiveMatch]);

  return (
    <div className="space-y-3 pt-3">
      {/* Main Content Area */}
      <div className="px-1 sm:px-2 py-2 space-y-2.5 max-w-3xl mx-auto">
        <div className="flex items-center justify-between gap-2">
          <h2 className="font-bold text-xl text-arena-900 dark:text-white/90">Live & Upcoming Matches</h2>
          <div className="flex items-center gap-2 shrink-0">
            <span className="inline-flex items-center gap-1.5 rounded-lg bg-white dark:bg-arena-800 border border-arena-200 dark:border-white/10 px-2.5 py-1 shadow-sm">
              <Icon name="account_balance_wallet" size={13} className="text-brand-500" />
              <span className="font-mono font-black text-xs text-arena-900 dark:text-white">{wallet.loading ? '…' : `₹${Number(wallet.balance).toLocaleString('en-IN')}`}</span>
            </span>
            <RulesButton compact />
          </div>
        </div>
        
        {loading ? (
          <div className="text-center py-10 text-arena-400 animate-pulse">Loading matches...</div>
        ) : matches.length === 0 ? (
          <div className="text-center py-10 text-arena-400">No matches found.</div>
        ) : (
          (() => {
            // Order: ACTIVELY-PLAYING live matches on top, just-finished next, then
            // live-but-IDLE multi-day matches (stumps/lunch/rain — running, not being
            // played right now) directly ABOVE the upcoming list, then upcoming
            // (first few, rest behind "Show more").
            const pricedLive = matches.filter((m) => m.status === 'in-play' && !m.play_state?.idle && m.has_odds);
            const unpricedLive = matches.filter((m) => m.status === 'in-play' && !m.play_state?.idle && !m.has_odds);
            const idleLive = matches.filter((m) => m.status === 'in-play' && m.play_state?.idle);
            const completedM = matches.filter((m) => m.status === 'completed');
            const upcomingM = matches.filter((m) => m.status === 'upcoming');
            const visibleUpcoming = showAllUpcoming ? upcomingM : upcomingM.slice(0, UPCOMING_LIMIT);
            // Bettable action first, finished results LAST (2026-08-06 UX sweep —
            // completed matches used to sit 2nd, pushing live/upcoming action down;
            // they're unbettable reference now, so they go to the bottom).
            const visible = [...pricedLive, ...unpricedLive, ...idleLive, ...visibleUpcoming, ...completedM];
            const hidden = upcomingM.length - visibleUpcoming.length;
            return (
              <>
                {visible.map(match => {
            const t1 = match.team_a || match.title.replace(/ \([^)]+\)/g, '').split(' vs ')[0] || 'Team 1';
            const t2 = match.team_b || match.title.replace(/ \([^)]+\)/g, '').split(' vs ')[1] || 'Team 2';
            const idle = match.status === 'in-play' && match.play_state?.idle;
            const clickable = match.status !== 'completed';
            const score = (idx: number) => {
              if (match.status !== 'completed' || !match.match_score) return null;
              try {
                const scores = typeof match.match_score === 'string' ? JSON.parse(match.match_score) : match.match_score;
                const s = scores[idx] || (idx === 1 ? scores[0] : null);
                return s ? `${s.r}/${s.w} (${s.o})` : null;
              } catch { return null; }
            };
            const dim = match.status === 'completed';
            const Flag = ({ img, name, crowned = false }: { img?: string; name: string; crowned?: boolean }) => (
              <span className="relative shrink-0">
                {img
                  ? <img src={img} alt={name} className={`w-10 h-10 object-cover rounded-full shadow-sm bg-white border ${crowned ? 'border-yellow-400 ring-1 ring-yellow-400/60' : 'border-arena-100 dark:border-white/10'} ${dim ? 'grayscale opacity-50' : ''}`} />
                  : <div className={`w-10 h-10 rounded-full bg-arena-100 dark:bg-arena-700 flex items-center justify-center font-black text-sm text-arena-400 ${dim ? 'grayscale opacity-50' : ''}`}>{name[0]}</div>}
                {crowned && <CrownBadge />}
              </span>
            );
            return (
              <div
                key={match.id}
                onClick={() => clickable && nav(`/games/${sport}/${match.id}`)}
                className={`rounded-xl px-3 py-2.5 border transition ${
                  match.status === 'in-play' && !idle && match.has_odds
                    // LIVE & RUNNING — the hero card: red ring glow, pull the eye
                    ? 'bg-gradient-to-br from-white to-red-50/70 dark:from-arena-800 dark:to-red-500/[0.07] border-red-300/70 dark:border-red-500/30 ring-1 ring-red-400/30 shadow-md shadow-red-500/10 cursor-pointer hover:ring-red-400/60 active:scale-[.99]'
                    : idle
                    // running but on a break — amber-edged, calm
                    ? 'bg-white dark:bg-arena-800 border-amber-300/60 dark:border-amber-500/25 shadow-sm cursor-pointer hover:bg-arena-50 dark:hover:bg-arena-700 active:scale-[.99]'
                    : match.status === 'completed'
                    // finished — visibly disabled: washed, dashed, greyed flags
                    ? 'bg-arena-50/80 dark:bg-arena-900/50 border-dashed border-arena-200 dark:border-white/10 shadow-none'
                    // upcoming — the clean look the owner already likes
                    : 'bg-white dark:bg-arena-800 border-arena-200 dark:border-white/5 shadow-sm cursor-pointer hover:bg-arena-50 dark:hover:bg-arena-700 active:scale-[.99]'
                }`}
              >
                {/* header: competition · format · subtitle | status chip */}
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] font-bold text-brand-600 dark:text-brand-400 tracking-wider uppercase truncate">
                    {match.competition_title
                      ? `${match.competition_title}${match.format_str ? ` · ${match.format_str}` : ''}${match.subtitle ? ` · ${match.subtitle}` : ''}`
                      : match.title.includes('T20') ? 'T20' : match.title.includes('ODI') ? 'ODI' : match.title.includes('Test') ? 'Test' : 'Cricket Match'}
                  </span>
                  {idle ? (
                    <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-500/20 text-[9px] font-bold text-amber-700 dark:text-amber-400 uppercase">
                      <Icon name="pause_circle" size={11} /> {match.play_state.stumps ? 'Stumps' : 'Break'}
                    </span>
                  ) : match.status === 'in-play' ? (
                    <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-500/20 text-[9px] font-bold text-red-600 dark:text-red-400">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> LIVE
                    </span>
                  ) : match.status === 'completed' ? (
                    <span className="shrink-0 px-1.5 py-0.5 rounded bg-green-500/20 text-[9px] font-bold text-green-700 dark:text-green-400 uppercase">Completed</span>
                  ) : (
                    <span className="shrink-0 px-1.5 py-0.5 rounded bg-blue-500/20 text-[9px] font-bold text-blue-600 dark:text-blue-400 uppercase">Upcoming</span>
                  )}
                </div>

                {/* flag-flanked center (competitor-inspired, our style): logos hold the
                    sides; names, time and countdown share the middle column */}
                <div className="mt-1.5 flex items-center gap-2.5">
                  <Flag img={match.team_a_img} name={t1} crowned={match.winner_side === 0} />
                  <div className="flex-1 min-w-0 flex flex-col items-center text-center gap-1">
                    <div className={`font-bold text-[13px] leading-snug ${dim ? 'text-arena-500 dark:text-arena-400' : 'text-arena-900 dark:text-white'}`}>
                      {t1} <span className={`text-[11px] font-black px-0.5 ${dim ? 'text-arena-400' : 'vs-heartbeat'}`}>{dim ? 'V/S' : 'V/S'}</span> {t2}
                    </div>
                    {match.status === 'completed' && (score(0) || score(1)) ? (
                      <div className="font-mono text-xs font-bold text-arena-500 dark:text-white/60">
                        {score(0) || '—'} <span className="text-arena-300 dark:text-arena-600">·</span> {score(1) || '—'}
                      </div>
                    ) : null}
                    <div className="inline-flex items-center gap-1 text-[10px] font-bold text-arena-400 dark:text-arena-300">
                      <Icon name="schedule" size={11} />
                      {fmtDateTime(match.start_time) || 'TBC'}
                      {idle ? <span className="text-amber-600 dark:text-amber-400"> · not being played</span>
                        : match.status === 'in-play' ? <span className="text-red-500"> · running</span>
                        : match.status === 'completed' ? <span className="text-green-600 dark:text-green-400"> · finished</span>
                        : null}
                    </div>
                    {/* Countdown only for upcoming; live/idle matches show the
                        odds row below instead of a big Bet-Live button (compact). */}
                    {match.status === 'upcoming' && (
                      <CountdownTimer dateStr={match.start_time} />
                    )}
                  </div>
                  <Flag img={match.team_b_img} name={t2} crowned={match.winner_side === 1} />
                </div>

                {/* 1 / X / 2 back(blue)/lay(pink) rates on EVERY match card (live
                    AND scheduled), competitor-style: real prices when live, locks
                    when suspended, dashed placeholders before a match is priced.
                    Live-updates via the /state poll. Not shown for finished matches. */}
                {match.status !== 'completed' && (
                  <LiveOddsRow state={oddsState[match.id]} team1={t1} team2={t2} />
                )}

                {/* one-line status tail (only when it adds information) */}
                {match.status === 'completed' && (
                  <div className="mt-1.5 flex items-center gap-1.5 rounded-lg bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/20 px-2 py-1">
                    <Icon name="emoji_events" size={12} className="text-yellow-500 shrink-0" />
                    <span className="text-[11px] font-bold text-green-800 dark:text-green-300 leading-tight">{match.match_result || 'Match Completed'}</span>
                  </div>
                )}
                {idle && (
                  <div className="mt-1.5 flex items-center gap-1.5 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 px-2 py-1">
                    <span className="text-[10px] font-bold text-amber-700 dark:text-amber-400 uppercase shrink-0">{match.play_state.label || 'Break'}</span>
                    <span className="text-[10px] font-bold text-arena-500 dark:text-arena-300 truncate">
                      {match.play_state.stumps ? `resumes ${expectedResume(match.start_time)}` : 'resumes shortly'} · betting open
                    </span>
                  </div>
                )}
              </div>
            );
          })}
                {hidden > 0 && (
                  <button
                    onClick={() => setShowAllUpcoming(true)}
                    className="w-full mt-1 py-3 rounded-xl bg-white dark:bg-arena-800 border border-arena-200 dark:border-white/10 font-black text-sm text-brand-600 dark:text-brand-400 hover:bg-arena-50 dark:hover:bg-arena-700 active:scale-[.99] transition flex items-center justify-center gap-2"
                  >
                    <Icon name="expand_more" size={18} /> Show {hidden} more upcoming
                  </button>
                )}
              </>
            );
          })()
        )}
      </div>
    </div>
  );
}

function LobbyView() {
  const [tab, setTab] = useState<'matches' | 'mybets' | 'pnl'>('matches');
  // Default the sport from the game route so the Football / Tennis tiles open on their tab.
  const loc = useLocation();
  const routeSport: 'cricket' | 'football' | 'tennis' =
    loc.pathname.includes('/games/football') ? 'football'
      : loc.pathname.includes('/games/tennis') ? 'tennis'
        : 'cricket';
  const [sport, setSport] = useState<'cricket' | 'football' | 'tennis'>(routeSport);
  const SPORTS = [
    { id: 'cricket' as const, label: 'Cricket', emoji: '🏏' },
    { id: 'football' as const, label: 'Football', emoji: '⚽' },
    { id: 'tennis' as const, label: 'Tennis', emoji: '🎾' },
  ];

  return (
    <div className="space-y-3 pt-3">
      {/* Sport selector — drives the Matches list. My Bets / My P&L stay all-sports. */}
      {tab === 'matches' && (
        <div className="px-4 mt-2">
          <div className="flex gap-2 max-w-3xl mx-auto">
            {SPORTS.map((s) => (
              <button
                key={s.id}
                onClick={() => setSport(s.id)}
                className={`flex-1 py-2 rounded-xl text-sm font-black tracking-wide border transition-all ${
                  sport === s.id
                    ? 'bg-brand-500 text-white border-brand-500 shadow'
                    : 'bg-white dark:bg-arena-800 text-arena-600 dark:text-arena-300 border-arena-200 dark:border-white/10 hover:border-brand-300'
                }`}
              >
                <span className="mr-1.5">{s.emoji}</span>{s.label}
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Top Tabs */}
      <div className="px-4 mt-2">
        <div className="flex p-1 bg-white dark:bg-arena-800 rounded-xl shadow-sm border border-arena-200 dark:border-white/5 max-w-3xl mx-auto">
          <button
            onClick={() => setTab('matches')}
            className={`flex-1 py-2.5 text-sm font-black uppercase tracking-wider rounded-lg transition-all ${
              tab === 'matches' 
                ? 'bg-arena-100 dark:bg-arena-700 text-brand-600 dark:text-brand-400' 
                : 'text-arena-500 dark:text-arena-400 hover:text-arena-700 dark:hover:text-arena-300'
            }`}
          >
            Matches
          </button>
          <button
            onClick={() => setTab('mybets')}
            className={`flex-1 py-2.5 text-sm font-black uppercase tracking-wider rounded-lg transition-all ${
              tab === 'mybets' 
                ? 'bg-arena-100 dark:bg-arena-700 text-brand-600 dark:text-brand-400' 
                : 'text-arena-500 dark:text-arena-400 hover:text-arena-700 dark:hover:text-arena-300'
            }`}
          >
            My Bets
          </button>
          <button
            onClick={() => setTab('pnl')}
            className={`flex-1 py-2.5 text-sm font-black uppercase tracking-wider rounded-lg transition-all ${
              tab === 'pnl'
                ? 'bg-arena-100 dark:bg-arena-700 text-brand-600 dark:text-brand-400'
                : 'text-arena-500 dark:text-arena-400 hover:text-arena-700 dark:hover:text-arena-300'
            }`}
          >
            My P&L
          </button>
        </div>
      </div>

      {tab === 'matches' ? <MatchList sport={sport} /> : (
        <div className="px-4">
          {tab === 'mybets' ? <MyBets /> : <Pnl />}
        </div>
      )}
    </div>
  );
}

export default function CricketPage() {
  return (
    <Routes>
      <Route path="/" element={<LobbyView />} />
      <Route path="/:matchId" element={<MatchPage />} />
    </Routes>
  );
}
